import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';

interface ProductCardProps {
  /** 商品 goods_id */
  id: string;
  /** 规格 ID */
  specId: string;
  name: string;
  code: string;
  price: number;
  unit: string;
  model: string;
  stock: number;
  supplier: string;
  image: string;
}

export default function ProductCard({
  id,
  specId,
  name,
  price,
  unit,
  model,
  stock,
  supplier,
  image,
}: ProductCardProps) {
  const { addItem, items } = useCart();
  const inCart = items.find((i) => i.productId === id && (i.specId || '') === (specId || ''));
  const cartQty = inCart?.quantity || 0;

  const handleAddToCart = (e: MouseEvent<HTMLButtonElement>) => {
    addItem(
      {
        productId: id,
        name,
        price,
        unit,
        image,
        stock,
        model,
        specId,
        specName: model,
      },
      { anchor: e.currentTarget },
    );
  };

  const stockUnlimited = stock < 0;
  const stockLabel = stockUnlimited
    ? '库存充足'
    : stock > 50
      ? '库存充足'
      : stock > 0
        ? `仅剩 ${stock} ${unit}`
        : '暂时缺货';
  const stockColor = stockUnlimited || stock > 50
    ? 'text-foreground-500'
    : stock > 0
      ? 'text-primary-600'
      : 'text-foreground-400';

  return (
    <div className="group bg-white rounded-lg border border-background-200 overflow-hidden hover:border-primary-300 hover:shadow-sm transition-all duration-300">
      <Link to={`/product/${id}`} className="block">
        <div className="relative aspect-square bg-background-50 overflow-hidden">
          <img
            src={image}
            alt={name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {stock > 0 && stock <= 10 && (
            <span className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-white text-xs rounded font-medium">
              即将售罄
            </span>
          )}
          {stock === 0 && (
            <div className="absolute inset-0 bg-foreground-950/40 flex items-center justify-center">
              <span className="px-3 py-1 bg-foreground-800 text-white text-xs rounded">暂时缺货</span>
            </div>
          )}
        </div>
      </Link>

      <div className="p-4 flex flex-col gap-2">
        <Link to={`/product/${id}`} className="block">
          <h3 className="text-sm font-medium text-foreground-900 leading-snug hover:text-primary-600 transition-colors line-clamp-2 min-h-[2.5rem]">
            {name}
          </h3>
        </Link>

        <div className="space-y-0.5">
          <p className="text-xs text-foreground-500 line-clamp-1">型号：{model}</p>
          <p className="text-xs text-foreground-400 line-clamp-1">{supplier}</p>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-background-200">
          <span className="text-lg font-bold text-primary-600 tracking-tight">¥{price.toFixed(2)}</span>
          <span className="text-sm text-accent-600 bg-background-100 px-2 py-0.5 rounded">/{unit}</span>
        </div>

        <p className={`text-xs text-right ${stockColor}`}>{stockLabel}</p>

        <button
          onClick={handleAddToCart}
          disabled={stock === 0 || !specId}
          className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-md text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap ${
            cartQty > 0
              ? 'bg-primary-500 text-white hover:bg-primary-600'
              : 'bg-background-200 text-foreground-600 hover:bg-background-300'
          }`}
        >
          <i className={cartQty > 0 ? 'ri-shopping-cart-fill' : 'ri-shopping-cart-line'} />
          {cartQty > 0 ? `已加入 (${cartQty})` : '加入购物车'}
        </button>
      </div>
    </div>
  );
}
